// EnvironmentsPOC.js — Documentation Fonctionnelle : POC « Environnements » (module Environnement).
// Aperçu interactif de la DISPOSITION RÉELLE de l'écran de gestion des Environnements :
//   en-tête bandeau dégradé (Rafraîchir + Nouveau) → 2 colonnes : liste (380px, recherche + cartes à menu …)
//   et panneau détail (en-tête + Activer/Désactiver + Modifier + onglets Informations / Fonctions / Agent),
//   plus les boîtes de dialogue Création/Édition, Assignation d'agent et Suppression.
// Les actions sont SIMULÉES localement (démo, aucun appel réseau / aucun apiService).
// Source de vérité : automata-studio-frontend [AUT][01]-Environments.js + automata_environment AutomataEnvironmentController.
import React, { useState } from 'react';
import {
  Plus, RefreshCw, Server, Edit, X, Power, PowerOff, Activity, CheckCircle, XCircle,
  Wifi, WifiOff, Hash, Layers, Box, Code, Globe, Zap, Cpu, Link, Info, Shield, ArrowRight,
  Folder, FileText, Trash2, MoreVertical, Upload, Download, Wand2,
} from 'lucide-react';
import { Card, CardContent, Button, Badge, Tabs, Icon, SearchBar, EmptyState } from '../ui';

// Types d'application proposés par le formulaire réel (libellés exacts de la liste déroulante).
const APP_TYPES = [
  { value: 'CORE_BANKING', label: 'Système bancaire' },
  { value: 'PAYMENT_SYSTEM', label: 'Système de paiement' },
  { value: 'TRADING_PLATFORM', label: 'Trading' },
  { value: 'ACCOUNTING_SYSTEM', label: 'Comptabilité' },
  { value: 'CRM', label: 'CRM' },
  { value: 'ERP', label: 'ERP' },
  { value: 'OTHER', label: 'Autre' },
];
const typeLabel = (v) => APP_TYPES.find((t) => t.value === v)?.label || v || 'N/A';

// Agents actifs proposés à l'assignation (démo).
const AVAILABLE_AGENTS = [
  { id: 11, agentType: 'GENERO_GDC', ipAddress: '10.0.8.70', port: 6400, agentVersion: '1.4.2', isActive: true },
  { id: 12, agentType: 'DATABASE_ORA', ipAddress: '10.20.3.10', port: 1521, agentVersion: '1.4.2', isActive: true },
  { id: 13, agentType: 'API_REST', ipAddress: '10.20.9.5', port: 8443, agentVersion: '1.4.0', isActive: true },
];

const SAMPLE = [
  {
    id: 1, appName: 'BGFI-Recette', appType: 'CORE_BANKING', appVersion: '11.2', isActive: true,
    agent: { id: 11, agentName: 'agent-gdc-recette', agentType: 'GENERO_GDC', ipAddress: '10.0.8.70', port: 6400, agentVersion: '1.4.2', isActive: true },
    functions: [
      { id: 101, level: '01', functionName: 'Comptabilité' },
      { id: 102, level: '0101', functionName: 'Saisie écriture' },
      { id: 103, level: '0102', functionName: 'Consultation grand-livre' },
      { id: 104, level: '02', functionName: 'Crédits' },
    ],
  },
  {
    id: 2, appName: 'CORE-Préprod', appType: 'PAYMENT_SYSTEM', appVersion: '19c', isActive: true,
    agent: null,
    functions: [{ id: 201, level: '01', functionName: 'Contrôle soldes' }],
  },
  {
    id: 3, appName: 'PORTAIL-Dev', appType: 'OTHER', appVersion: '2.4', isActive: false,
    agent: null, functions: [],
  },
];
const EMPTY_FORM = { appName: '', appType: '', appVersion: '', isActive: true };

// ---- Petits sous-composants de la réplique ---------------------------------
const EnvIcon = ({ active, box = 'h-10 w-10', size = 'h-5 w-5' }) => (
  <span className={`${box} rounded-xl flex items-center justify-center shrink-0 ${active ? 'bg-gradient-to-br from-emerald-400 to-emerald-600' : 'bg-gray-200 dark:bg-gray-700'}`}>
    <Server className={`${size} ${active ? 'text-white' : 'text-gray-500'}`} />
  </span>
);

const StatCard = ({ icon: IconCmp, label, value }) => (
  <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1"><IconCmp className="h-4 w-4" />{label}</div>
    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 break-all">{value || 'N/A'}</div>
  </div>
);

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

// ---- Page -------------------------------------------------------------------
const EnvironmentsPOC = () => {
  const [environments, setEnvironments] = useState(SAMPLE);
  const [selectedId, setSelectedId] = useState(SAMPLE[0].id);
  const [tab, setTab] = useState('info'); // 'info' | 'functions' | 'agent'
  const [search, setSearch] = useState('');

  const [dialog, setDialog] = useState(null); // null | 'create' | 'edit' | 'assign' | 'delete'
  const [form, setForm] = useState(EMPTY_FORM);
  const [agentChoice, setAgentChoice] = useState('');
  const [menuId, setMenuId] = useState(null); // carte dont le menu … est ouvert
  const [test, setTest] = useState('idle'); // 'idle' | 'testing' | 'ok' | 'ko'
  const [toast, setToast] = useState('');

  const selected = environments.find((e) => e.id === selectedId) || null;
  const filtered = environments.filter((e) =>
    `${e.appName} ${typeLabel(e.appType)} ${e.appVersion}`.toLowerCase().includes(search.toLowerCase())
  );

  const flash = (msg) => { setToast(msg); window.setTimeout(() => setToast(''), 2200); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));
  const closeDialog = () => setDialog(null);

  const openCreate = () => { setForm(EMPTY_FORM); setDialog('create'); };
  const openEdit = (env) => {
    const t = env || selected;
    if (!t) return;
    setSelectedId(t.id);
    setForm({ appName: t.appName, appType: t.appType, appVersion: t.appVersion, isActive: t.isActive });
    setMenuId(null); setDialog('edit');
  };
  const submitCreate = () => {
    if (!form.appName || !form.appType) return;
    const id = Math.max(0, ...environments.map((e) => e.id)) + 1;
    setEnvironments((prev) => [...prev, { ...form, id, agent: null, functions: [] }]);
    setSelectedId(id); setTab('info'); setDialog(null); flash('Environnement créé avec succès');
  };
  const submitEdit = () => {
    if (!form.appName || !form.appType) return;
    setEnvironments((prev) => prev.map((e) => e.id === selectedId ? { ...e, ...form } : e));
    setDialog(null); flash('Environnement mis à jour');
  };
  const toggleActive = (env) => {
    const t = env || selected; if (!t) return;
    setEnvironments((prev) => prev.map((e) => e.id === t.id ? { ...e, isActive: !e.isActive } : e));
    setMenuId(null); flash(t.isActive ? 'Environnement désactivé' : 'Environnement activé');
  };
  const openAssign = (env) => {
    if (env) setSelectedId(env.id);
    setAgentChoice(''); setMenuId(null); setDialog('assign');
  };
  const assignAgent = () => {
    const ag = AVAILABLE_AGENTS.find((a) => String(a.id) === String(agentChoice));
    if (!ag) return;
    setEnvironments((prev) => prev.map((e) => e.id === selectedId ? { ...e, agent: { ...ag, agentName: `agent-${ag.id}` } } : e));
    setDialog(null); setAgentChoice(''); setTest('idle'); flash('Agent assigné avec succès');
  };
  const unassignAgent = (env) => {
    const t = env || selected; if (!t) return;
    setEnvironments((prev) => prev.map((e) => e.id === t.id ? { ...e, agent: null } : e));
    setMenuId(null); setTest('idle'); flash("Agent déconnecté de l'environnement");
  };
  const askDelete = (env) => { if (env) setSelectedId(env.id); setMenuId(null); setDialog('delete'); };
  const confirmDelete = () => {
    if (selected?.functions.length) { setDialog(null); flash('Suppression refusée : des fonctions sont rattachées'); return; }
    setEnvironments((prev) => prev.filter((e) => e.id !== selectedId));
    setSelectedId((prev) => { const rest = environments.filter((e) => e.id !== prev); return rest[0]?.id ?? null; });
    setDialog(null); flash('Environnement supprimé');
  };
  const runTest = () => {
    setTest('testing');
    const ok = !!selected?.agent?.isActive;
    window.setTimeout(() => setTest(ok ? 'ok' : 'ko'), 700);
  };

  return (
    <div className="space-y-8">
      {/* (1) Intro */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Environnements</h1>
          <Badge color="#3B82F6">Module Environnement</Badge>
          <Badge color="#F59E0B">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Aperçu interactif de l'écran <strong>Environnements</strong> (démo, actions simulées localement).
        </p>
      </div>

      {/* (2) Réplique interactive FIDÈLE à la disposition réelle */}
      <Card className="overflow-hidden">
        {/* En-tête : bandeau dégradé + Rafraîchir + Nouveau */}
        <div className="px-5 py-4 bg-gradient-to-r from-blue-500 to-indigo-700 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center"><Server className="h-5 w-5" /></span>
            <div>
              <h2 className="text-2xl font-bold leading-tight">Environnements</h2>
              <p className="text-sm text-blue-100">{environments.length} environnement{environments.length > 1 ? 's' : ''} configuré{environments.length > 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => flash('Liste actualisée')} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors" title="Rafraîchir"><RefreshCw className="h-5 w-5" /></button>
            <Button onClick={openCreate} className="bg-white/20 hover:bg-white/30 text-white"><Plus className="h-4 w-4" />Nouveau</Button>
          </div>
        </div>

        {/* Bandeau de notification (démo — équivalent du Snackbar réel) */}
        {toast && (
          <div className="px-5 py-2 bg-emerald-50 dark:bg-emerald-500/10 border-b border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-sm flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />{toast}
          </div>
        )}

        <div className="flex flex-col lg:flex-row">
          {/* Panneau gauche : recherche + liste (380px) */}
          <div className="lg:w-[380px] shrink-0 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40">
            <div className="p-3">
              <SearchBar value={search} onChange={setSearch} placeholder="Rechercher..." />
            </div>
            <div className="px-3 pb-3 space-y-2 max-h-[480px] overflow-y-auto">
              {filtered.map((e) => {
                const on = selected?.id === e.id;
                return (
                  <div
                    key={e.id}
                    onClick={() => { setSelectedId(e.id); setTab('info'); setTest('idle'); }}
                    className={`relative group w-full text-left p-3 rounded-xl border cursor-pointer transition-all ${on ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <EnvIcon active={e.isActive} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm text-gray-900 dark:text-white truncate">{e.appName}</span>
                            <span className={`inline-block h-2 w-2 rounded-full shrink-0 ${e.isActive ? 'bg-emerald-400' : 'bg-gray-400'}`} />
                          </div>
                          <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                            <span className="truncate">{typeLabel(e.appType)}</span>{e.appVersion && <Badge>v{e.appVersion}</Badge>}
                          </div>
                        </div>
                      </div>
                      {/* Menu contextuel … par carte */}
                      <div className="relative shrink-0">
                        <button
                          onClick={(ev) => { ev.stopPropagation(); setMenuId(menuId === e.id ? null : e.id); }}
                          className={`p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${on || menuId === e.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                        {menuId === e.id && (
                          <div onClick={(ev) => ev.stopPropagation()} className="absolute right-0 top-8 z-20 w-56 py-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl text-sm">
                            <button onClick={() => openEdit(e)} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60"><Edit className="h-4 w-4" />Modifier</button>
                            <button onClick={() => toggleActive(e)} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60">{e.isActive ? <><PowerOff className="h-4 w-4" />Désactiver</> : <><Power className="h-4 w-4" />Activer</>}</button>
                            <button onClick={() => openAssign(e)} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60"><Link className="h-4 w-4" />Assigner un agent</button>
                            {e.agent && <button onClick={() => unassignAgent(e)} className="w-full flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-gray-50 dark:hover:bg-gray-700/60"><WifiOff className="h-4 w-4" />Déconnecter l'agent</button>}
                            <button onClick={() => { setMenuId(null); flash('Import CSV (assistant 4 étapes)'); }} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60"><Upload className="h-4 w-4" />Importer fonctionnalités (CSV)</button>
                            {e.agent && <button onClick={() => { setMenuId(null); flash('Chargement programmes via l\'agent'); }} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60"><Download className="h-4 w-4" />Charger programmes (Agent)</button>}
                            {e.agent && <button onClick={() => { setMenuId(null); flash('Génération de scénarios (4GL)'); }} className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60"><Wand2 className="h-4 w-4" />Générer scénarios (4GL)</button>}
                            <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
                            <button onClick={() => askDelete(e)} className="w-full flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-gray-50 dark:hover:bg-gray-700/60"><Trash2 className="h-4 w-4" />Supprimer</button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                      {e.isActive ? <Badge color="#10B981"><CheckCircle className="h-3 w-3" />Actif</Badge> : <Badge><XCircle className="h-3 w-3" />Inactif</Badge>}
                      {e.agent ? <Badge color="#3B82F6"><Wifi className="h-3 w-3" />Agent déployé</Badge> : <Badge color="#F59E0B"><WifiOff className="h-3 w-3" />Sans agent</Badge>}
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <EmptyState icon={Server} title="Aucun environnement" message="Modifiez votre recherche ou créez un nouvel environnement." />
              )}
            </div>
          </div>

          {/* Panneau droit : détail (toujours visible, formulaire = boîte de dialogue séparée) */}
          <div className="flex-1 min-w-0">
            {selected ? (
              <div>
                {/* En-tête détail + actions */}
                <div className="px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-white to-gray-50 dark:from-gray-800 dark:to-gray-800/60">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-4 min-w-0">
                      <EnvIcon active={selected.isActive} box="h-12 w-12 rounded-2xl" size="h-6 w-6" />
                      <div className="min-w-0">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate">{selected.appName}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{typeLabel(selected.appType)}{selected.appVersion && <span> · v{selected.appVersion}</span>}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {selected.isActive
                        ? <Button variant="ghost" size="sm" onClick={() => toggleActive()} className="text-red-600"><PowerOff className="h-4 w-4" />Désactiver</Button>
                        : <Button variant="ghost" size="sm" onClick={() => toggleActive()} className="text-emerald-600"><Power className="h-4 w-4" />Activer</Button>}
                      <Button variant="outline" size="sm" onClick={() => openEdit()}><Edit className="h-4 w-4" />Modifier</Button>
                    </div>
                  </div>
                  {/* Onglets à icônes */}
                  <Tabs
                    tabs={[
                      { id: 'info', label: 'Informations' },
                      { id: 'functions', label: 'Fonctions', count: selected.functions.length },
                      { id: 'agent', label: 'Agent' },
                    ]}
                    active={tab}
                    onChange={setTab}
                    className="mt-4 border-b-0"
                  />
                </div>

                <div className="p-5">
                  {/* Onglet Informations : 4 StatCards + table de détail */}
                  {tab === 'info' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <StatCard icon={Hash} label="Identifiant" value={selected.id} />
                        <StatCard icon={Layers} label="Type" value={typeLabel(selected.appType)} />
                        <StatCard icon={Box} label="Version" value={selected.appVersion} />
                        <StatCard icon={Code} label="Fonctions" value={selected.functions.length} />
                      </div>
                      <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Détails de l'environnement</h4>
                        </div>
                        <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
                          {[
                            ["Nom de l'application", selected.appName],
                            ["Type d'application", typeLabel(selected.appType)],
                            ['Version', selected.appVersion || 'N/A'],
                            ['Statut', selected.isActive ? <Badge color="#10B981"><CheckCircle className="h-3 w-3" />Actif</Badge> : <Badge color="#EF4444"><XCircle className="h-3 w-3" />Inactif</Badge>],
                            ['Agent', selected.agent ? <Badge color="#3B82F6"><Wifi className="h-3 w-3" />Déployé</Badge> : <Badge color="#F59E0B"><WifiOff className="h-3 w-3" />Non déployé</Badge>],
                          ].map(([l, v], i) => (
                            <div key={i} className="flex items-center justify-between px-4 py-3 text-sm">
                              <span className="text-gray-500 dark:text-gray-400">{l}</span>
                              <span className="font-medium text-gray-900 dark:text-white">{v}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Onglet Fonctions : en-tête (compteur + boutons) + arbre hiérarchique */}
                  {tab === 'functions' && (
                    selected.functions.length === 0 ? (
                      <EmptyState icon={Code} title="Aucune fonction" message="Aucune fonction associée à cet environnement. Importez un référentiel (CSV) ou chargez-le via l'agent." />
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Fonctions associées</h4>
                            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded-full">{selected.functions.length}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {selected.agent && (
                              <>
                                <button onClick={() => flash('Chargement programmes via l\'agent')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 rounded-lg hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 transition-colors"><Download className="h-3.5 w-3.5" />Charger (Agent)</button>
                                <button onClick={() => flash('Génération de scénarios (4GL)')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-600 bg-amber-50 rounded-lg hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-400 transition-colors"><Wand2 className="h-3.5 w-3.5" />Générer scénarios</button>
                              </>
                            )}
                            <button onClick={() => flash('Import CSV (assistant 4 étapes)')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 transition-colors"><Upload className="h-3.5 w-3.5" />Importer (CSV)</button>
                          </div>
                        </div>
                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/60 max-h-[420px] overflow-y-auto">
                          {selected.functions.map((f) => {
                            const depth = Math.max(0, (f.level?.length || 2) / 2 - 1);
                            const isRoot = depth === 0;
                            return (
                              <div key={f.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700/40" style={{ paddingLeft: `${0.75 + depth * 1.25}rem` }}>
                                {isRoot ? <Folder className="h-4 w-4 text-blue-500 shrink-0" /> : <FileText className="h-4 w-4 text-gray-400 shrink-0" />}
                                <span className="text-gray-800 dark:text-gray-200 truncate">{f.functionName}</span>
                                <span className="ml-auto text-[10px] font-mono text-gray-400">niv. {f.level}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )
                  )}

                  {/* Onglet Agent : bannière statut + carte agent (StatCards + actions) */}
                  {tab === 'agent' && (
                    <div className="space-y-4">
                      {selected.agent ? (
                        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-sm">
                          <CheckCircle className="h-4 w-4" />Agent déployé sur cet environnement
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-300 text-sm">
                          <WifiOff className="h-4 w-4" />Aucun agent déployé
                        </div>
                      )}

                      {selected.agent ? (
                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                              <span className="h-10 w-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-blue-400 to-indigo-600"><Cpu className="h-5 w-5 text-white" /></span>
                              <div>
                                <div className="text-sm font-semibold text-gray-900 dark:text-white">{selected.agent.agentName || selected.agent.ipAddress}</div>
                                <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                                  <span className={`inline-block h-2 w-2 rounded-full ${selected.agent.isActive ? 'bg-emerald-400' : 'bg-gray-400'}`} />
                                  {selected.agent.isActive ? 'Actif' : 'Inactif'}
                                </div>
                              </div>
                            </div>
                            {test === 'ok' && <Badge color="#10B981"><Wifi className="h-3 w-3" />Connecté</Badge>}
                            {test === 'ko' && <Badge color="#EF4444"><WifiOff className="h-3 w-3" />Déconnecté</Badge>}
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4">
                            <StatCard icon={Server} label="Type d'agent" value={selected.agent.agentType} />
                            <StatCard icon={Globe} label="Adresse IP" value={selected.agent.ipAddress} />
                            <StatCard icon={Zap} label="Port" value={selected.agent.port} />
                            <StatCard icon={Shield} label="Version" value={selected.agent.agentVersion} />
                          </div>
                          <div className="px-4 pb-2"><span className="text-[10px] font-mono text-gray-400 dark:text-gray-500">{selected.agent.id}</span></div>
                          <div className="flex flex-wrap items-center gap-2 p-4 pt-2 border-t border-gray-100 dark:border-gray-700">
                            <Button variant="outline" size="sm" onClick={runTest} disabled={test === 'testing'}>
                              {test === 'testing' ? <>Test en cours…</> : <><Activity className="h-4 w-4" />Tester la connexion</>}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => openAssign()}><Link className="h-4 w-4" />Réassigner</Button>
                          </div>
                        </div>
                      ) : (
                        <EmptyState icon={Cpu} title="Aucun agent assigné" message="Assignez un agent pour pouvoir exécuter des tests sur cet environnement." className="!py-10" />
                      )}
                      {!selected.agent && (
                        <div className="flex justify-center">
                          <Button onClick={() => openAssign()}><Link className="h-4 w-4" />Assigner un agent</Button>
                        </div>
                      )}
                      <p className="text-xs text-gray-400">Démo : un agent <strong>actif</strong> renvoie « Connecté » au test, sinon « Déconnecté ».</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <EmptyState icon={Layers} title="Sélectionnez un environnement" message="Choisissez un environnement dans la liste pour afficher ses détails." className="!py-24" />
            )}
          </div>
        </div>
      </Card>

      {/* Boîte de dialogue Création / Édition (modale) */}
      {(dialog === 'create' || dialog === 'edit') && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={closeDialog}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-2xl bg-white dark:bg-gray-800 shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">{dialog === 'create' ? 'Nouvel environnement' : "Modifier l'environnement"}</h3>
              <button onClick={closeDialog} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <label className="block text-sm">
                <span className="block text-gray-700 dark:text-gray-300 mb-1">Nom de l'application <span className="text-red-500">*</span></span>
                <input value={form.appName} onChange={set('appName')} placeholder="BGFI-Recette" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1">Type <span className="text-red-500">*</span></span>
                  <select value={form.appType} onChange={set('appType')} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none">
                    <option value="">— Choisir —</option>
                    {APP_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1">Version <span className="text-red-500">*</span></span>
                  <input value={form.appVersion} onChange={set('appVersion')} placeholder="11.2" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none" />
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} className="h-4 w-4" />
                Actif <span className="text-gray-400">({form.isActive ? 'Actif' : 'Inactif'})</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-700">
              <Button variant="ghost" onClick={closeDialog}>Annuler</Button>
              <Button onClick={dialog === 'create' ? submitCreate : submitEdit} disabled={!form.appName || !form.appType}>
                {dialog === 'create' ? <><Plus className="h-4 w-4" />Créer</> : <><Edit className="h-4 w-4" />Enregistrer</>}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Boîte de dialogue Assigner un agent (modale) */}
      {dialog === 'assign' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={closeDialog}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-2xl">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Assigner un agent</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-300">Sélectionnez un agent pour <strong>{selected?.appName}</strong></p>
              <select value={agentChoice} onChange={(e) => setAgentChoice(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm">
                <option value="">— Choisir un agent —</option>
                {AVAILABLE_AGENTS.map((a) => <option key={a.id} value={a.id}>{a.agentType} - {a.ipAddress}:{a.port}</option>)}
              </select>
              <p className="text-xs text-gray-400">Seuls les agents <strong>actifs</strong> sont assignables (règle backend).</p>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-700">
              <Button variant="ghost" onClick={closeDialog}>Annuler</Button>
              <Button onClick={assignAgent} disabled={!agentChoice}><Link className="h-4 w-4" />Assigner</Button>
            </div>
          </div>
        </div>
      )}

      {/* Boîte de dialogue Confirmer la suppression (modale) */}
      {dialog === 'delete' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={closeDialog}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-2xl">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-bold text-red-600">Confirmer la suppression</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-700 dark:text-gray-200">Supprimer <strong>{selected?.appName}</strong> ? Cette action est irréversible.</p>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
                <Info className="h-4 w-4 text-amber-500 shrink-0" />
                <span className="text-xs text-amber-700 dark:text-amber-400">Si cet environnement contient des fonctions, la suppression échouera (HTTP 409).</span>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-700">
              <Button variant="ghost" onClick={closeDialog}>Annuler</Button>
              <Button variant="danger" onClick={confirmDelete}><Trash2 className="h-4 w-4" />Supprimer</Button>
            </div>
          </div>
        </div>
      )}

      {/* (3) Guide utilisateur */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Plus className="h-5 w-5 text-blue-500" />Créer un environnement</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Cliquer sur <strong>« Nouveau »</strong> (en haut à droite du bandeau). <button onClick={openCreate} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={2}>Saisir le <strong>Nom de l'application</strong> et choisir le <strong>Type</strong> — champs obligatoires.</GuideStep>
              <GuideStep n={3}>Renseigner la <strong>Version</strong> et l'état <strong>Actif</strong>.</GuideStep>
              <GuideStep n={4}>Valider avec <strong>Créer</strong>.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Edit className="h-5 w-5 text-blue-500" />Configurer un environnement</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Sélectionner un environnement dans la liste de gauche.</GuideStep>
              <GuideStep n={2}>Onglet <strong>Informations</strong> : <strong>Modifier</strong> ou <strong>Activer / Désactiver</strong>. <button onClick={() => setTab('info')} className="text-blue-600 dark:text-blue-400 underline">Voir</button></GuideStep>
              <GuideStep n={3}>Onglet <strong>Fonctions</strong> : consulter / importer (CSV) ou charger via l'agent. <button onClick={() => setTab('functions')} className="text-blue-600 dark:text-blue-400 underline">Ouvrir</button></GuideStep>
              <GuideStep n={4}>Le menu <strong>…</strong> de chaque carte regroupe toutes les actions (dont Supprimer).</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Link className="h-5 w-5 text-blue-500" />Déployer un agent</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Ouvrir l'onglet <strong>Agent</strong> de l'environnement. <button onClick={() => setTab('agent')} className="text-blue-600 dark:text-blue-400 underline">Ouvrir</button></GuideStep>
              <GuideStep n={2}>Cliquer <strong>« Assigner un agent »</strong>, choisir un <strong>agent actif</strong> puis <strong>Assigner</strong>.</GuideStep>
              <GuideStep n={3}>Cliquer <strong>« Tester la connexion »</strong> et lire le résultat (<span className="text-emerald-600">Connecté</span> / <span className="text-red-600">Déconnecté</span>).</GuideStep>
              <GuideStep n={4}>Au besoin, <strong>Réassigner</strong> ou <strong>Déconnecter</strong> (menu …).</GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* (4) Référence */}
      <Card><CardContent>
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Info className="h-5 w-5 text-blue-500" />Référence des champs du formulaire</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
              <tr>{['Champ', 'Type', 'Requis', 'Détails'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
              {[
                ["Nom de l'application", 'Texte', 'Oui', 'Libellé de l\'environnement (ex. BGFI-Recette)'],
                ["Type d'application", 'Liste', 'Oui', 'Système bancaire, Système de paiement, Trading, Comptabilité, CRM, ERP, Autre'],
                ['Version', 'Texte', 'Oui', 'Version applicative (ex. 11.2, 19c)'],
                ['Actif', 'Interrupteur', 'Non', 'Actif par défaut ; un environnement inactif n\'est pas exécutable'],
                ['Agent', 'Association', 'Non', 'Agent actif assigné séparément (onglet Agent / menu …)'],
              ].map((r, i) => (
                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2 font-medium">{r[0]}</td><td className="px-4 py-2">{r[1]}</td>
                  <td className="px-4 py-2">{r[2] === 'Oui' ? <Badge color="#EF4444">Oui</Badge> : <Badge>Non</Badge>}</td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{r[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Colonnes de la liste</h4>
            <ul className="space-y-1.5 text-sm text-gray-700 dark:text-gray-300">
              <li className="flex items-center gap-2"><Server className="h-4 w-4 text-gray-400" />Icône d'état + Nom de l'application + point de statut</li>
              <li className="flex items-center gap-2"><Layers className="h-4 w-4 text-gray-400" />Type d'application + badge version</li>
              <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-gray-400" />Badge Actif / Inactif</li>
              <li className="flex items-center gap-2"><Wifi className="h-4 w-4 text-gray-400" />Badge Agent déployé / Sans agent</li>
              <li className="flex items-center gap-2"><MoreVertical className="h-4 w-4 text-gray-400" />Menu … (Modifier, (Dés)activer, Assigner/Déconnecter, Import CSV, Charger/Générer, Supprimer)</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Statuts</h4>
            <ul className="space-y-1.5 text-sm text-gray-700 dark:text-gray-300">
              <li className="flex items-center gap-2"><Badge color="#10B981"><CheckCircle className="h-3 w-3" />Actif</Badge> environnement exécutable</li>
              <li className="flex items-center gap-2"><Badge color="#EF4444"><XCircle className="h-3 w-3" />Inactif</Badge> non exécutable</li>
              <li className="flex items-center gap-2"><Badge color="#3B82F6"><Wifi className="h-3 w-3" />Agent déployé</Badge> agent rattaché</li>
              <li className="flex items-center gap-2"><Badge color="#F59E0B"><WifiOff className="h-3 w-3" />Sans agent</Badge> aucun agent</li>
            </ul>
          </div>
        </div>
      </CardContent></Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Règles de gestion</h3>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Le <strong>Nom de l'application</strong> et le <strong>Type</strong> sont obligatoires.</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />Un environnement <strong>rattaché à des fonctions</strong> ne peut pas être supprimé (HTTP 409 — les retirer d'abord).</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />Seul un <strong>agent actif</strong> peut être assigné (HTTP 412 sinon).</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />L'exécution de tests exige un environnement <strong>actif</strong> avec un <strong>agent déployé</strong>.</li>
          </ul>
        </CardContent></Card>

        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Endpoints API</h3>
          <ul className="space-y-1.5 text-sm font-mono text-gray-700 dark:text-gray-200">
            {[
              ['GET', '#10B981', '/api/automata/environments'],
              ['POST', '#3B82F6', '/api/automata/environments'],
              ['PUT', '#F59E0B', '/api/automata/environments/{id}'],
              ['DELETE', '#EF4444', '/api/automata/environments/{id}'],
              ['PATCH', '#8B5CF6', '/api/automata/environments/{id}/active?active='],
              ['GET', '#10B981', '/api/automata/environments/{id}/functions'],
              ['GET', '#10B981', '/api/automata/environments/agents/active'],
              ['POST', '#3B82F6', '/api/automata/environments/{id}/assign-agent?agentId='],
              ['DELETE', '#EF4444', '/api/automata/environments/{id}/agent'],
              ['GET', '#10B981', '/api/automata/environments/{id}/agent-deployed'],
            ].map((r, i) => (
              <li key={i} className="flex items-center gap-2"><Badge color={r[1]}>{r[0]}</Badge><span className="break-all">{r[2]}</span></li>
            ))}
          </ul>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><ArrowRight className="h-3 w-3" />Source : automata_environment / AutomataEnvironmentController</p>
        </CardContent></Card>
      </div>
    </div>
  );
};

export default EnvironmentsPOC;
